import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, Check, ArrowRight } from 'lucide-react';
import { usePricingPopup } from '../contexts/PricingPopupContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { trackAddToCart } from '../utils/metaPixel';
const REVIEWS = [{
  text: 'This literally saved our evenings. The first night was hard, but by night 3, he just handed me the pass and went to sleep.',
  author: 'Sarah M.',
  role: 'Mom of a 5-year-old'
}, {
  text: "I didn't think a piece of paper would work, but the 'silent return' is magic. No more 45-minute negotiations.",
  author: 'David K.',
  role: 'Dad of two'
}, {
  text: 'Simple, gentle, and actually works. I feel like I have my evenings back for the first time in years.',
  author: 'Elena R.',
  role: 'Mom of a 4-year-old'
}];
const ORIGINAL_PRICE_USD = 12.99;
export function PricingPopup() {
  const {
    isOpen,
    closePopup
  } = usePricingPopup();
  const {
    formattedPrice,
    currency
  } = useCurrency();
  const [currentReview, setCurrentReview] = useState(0);
  // Calculate "was" price based on current currency rate
  const wasPrice = (ORIGINAL_PRICE_USD * currency.rate).toLocaleString(undefined, {
    style: 'currency',
    currency: currency.code,
    minimumFractionDigits: currency.code === 'JPY' || currency.code === 'KRW' ? 0 : 2,
    maximumFractionDigits: currency.code === 'JPY' || currency.code === 'KRW' ? 0 : 2
  });
  // Auto-rotate reviews
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      setCurrentReview(prev => (prev + 1) % REVIEWS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isOpen]);
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-135">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={closePopup} className="fixed inset-0 bg-[#1e2a4a]/60 backdrop-blur-sm z-[100]" data-id="element-136" />

          {/* Modal */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none" data-id="element-137">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-[#faf6ef] w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden pointer-events-auto relative border border-[#c9a96e]/20" data-id="element-138">
              {/* Close Button */}
              <button onClick={closePopup} className="absolute top-4 right-4 p-2 text-[#6b7fa3] hover:text-[#1e2a4a] transition-colors z-10" aria-label="Close popup" data-id="element-139">
                <X size={24} data-id="element-140" />
              </button>

              {/* Header */}
              <div className="bg-[#1e2a4a] p-8 text-center relative overflow-hidden" data-id="element-141">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#c9a96e]/0 via-[#c9a96e] to-[#c9a96e]/0 opacity-50" data-id="element-142" />
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#faf6ef] mb-2" data-id="element-143">
                  Get Your Evenings Back
                </h2>
                <p className="text-[#c9a96e] font-serif italic" data-id="element-144">
                  Instant Digital Download • Print & Start Tonight
                </p>
              </div>

              <div className="p-6 md:p-8" data-id="element-145">
                {/* Rotating Reviews */}
                <div className="mb-8 min-h-[140px] relative" data-id="element-146">
                  <AnimatePresence mode="wait" data-id="element-147">
                    <motion.div key={currentReview} initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.3
                }} className="text-center" data-id="element-148">
                      <div className="flex justify-center gap-1 mb-3 text-[#c9a96e]" data-id="element-149">
                        {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-150" />)}
                      </div>
                      <p className="text-[#1e2a4a]/80 text-lg mb-3 italic leading-relaxed" data-id="element-151">
                        "{REVIEWS[currentReview].text}"
                      </p>
                      <p className="text-sm font-bold text-[#1e2a4a]" data-id="element-152">
                        — {REVIEWS[currentReview].author},{' '}
                        <span className="font-normal text-[#6b7fa3]" data-id="element-153">
                          {REVIEWS[currentReview].role}
                        </span>
                      </p>
                    </motion.div>
                  </AnimatePresence>

                  {/* Dots */}
                  <div className="flex justify-center gap-2 mt-4" data-id="element-154">
                    {REVIEWS.map((_, i) => <button key={i} onClick={() => setCurrentReview(i)} className={`w-2 h-2 rounded-full transition-colors ${i === currentReview ? 'bg-[#c9a96e]' : 'bg-[#c9a96e]/30'}`} aria-label={`Go to review ${i + 1}`} data-id="element-155" />)}
                  </div>
                </div>

                {/* Pricing & CTA */}
                <div className="bg-[#fffdf8] rounded-xl p-6 border border-[#c9a96e]/20 text-center" data-id="element-156">
                  <div className="flex items-center justify-center gap-3 mb-2" data-id="element-157">
                    <span className="text-[#6b7fa3] line-through text-lg" data-id="element-158">
                      {wasPrice}
                    </span>
                    <span className="text-[#1e2a4a] font-bold text-3xl font-serif" data-id="element-159">
                      {formattedPrice}
                    </span>
                  </div>
                  <div className="inline-block bg-[#c9a96e]/10 text-[#c9a96e] text-xs font-bold px-2 py-1 rounded mb-6 border border-[#c9a96e]/20" data-id="element-160">
                    LIMITED TIME OFFER
                  </div>

                  <ul className="text-left space-y-2 mb-6 max-w-xs mx-auto text-sm text-[#1e2a4a]/80" data-id="element-161">
                    <li className="flex items-start gap-2" data-id="element-162">
                      <Check size={16} className="text-[#c9a96e] mt-0.5 shrink-0" data-id="element-163" />
                      <span data-id="element-164">Bedtime Pass & Script Cards</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-165">
                      <Check size={16} className="text-[#c9a96e] mt-0.5 shrink-0" data-id="element-166" />
                      <span data-id="element-167">Silent Return Protocol Guide</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-168">
                      <Check size={16} className="text-[#c9a96e] mt-0.5 shrink-0" data-id="element-169" />
                      <span data-id="element-170">Instant PDF Access</span>
                    </li>
                  </ul>

                  <a href="https://www.gemmamama.com/cart/45504434274443:1" target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} className="w-full group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-[#1e2a4a] transition-all duration-200 bg-[#c9a96e] rounded-full hover:bg-[#d4b67d] shadow-lg shadow-[#c9a96e]/20" data-id="element-171">
                    Download Now
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-172" />
                  </a>

                  <p className="mt-3 text-xs text-[#6b7fa3]" data-id="element-173">
                    Secure checkout • 100% Money-back guarantee
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}